// Copyright 2018 Sorama B.V.

import { autoinject, bindable } from "aurelia-framework";
import { PlotData } from "controls/plot/graph-plot/GraphPlot";
import { AxisKind } from "datatypes/SettingsKinds/AxisKinds";
import { CSVDataExporter } from "libraries/Export/CSVDataExporter";
import { Series } from "libraries/Export/IDataExporter";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";

@autoinject
export class CSVExportModal implements IModal<CSVExportModalState> {
    private checkAllCheckbox: HTMLInputElement;

    @bindable
    private spectra: PlotData[];
    private selectedSpectra: PlotData[];
    private selectedFrequencyAxisKind: AxisKind;
    private selectedWeighting: string;

    private selectedMeasurementNames: { [name: string]: boolean } = {};
    private anySelected: boolean;
    private allSelected: boolean;
    private state: CSVExportModalState;
    private modalInstance: IWorkflowModuleModalInstance<CSVExportModalState>;

    public activate(model?: CSVExportModalModel) {
        if (model != undefined) {
            this.activateComponents(model.state, model.moduleInstance);
        }
    }

    private activateComponents(state, moduleInstance) {
        this.state = state;
        this.modalInstance = moduleInstance;

        if (state.spectra) {
            this.spectra = state.spectra;
            this.selectedFrequencyAxisKind = state.selectedFrequencyAxisKind;
            this.selectedWeighting = state.selectedWeighting;
        }
    }

    public close(): CSVExportModalState {
        return this.state;
    }

    private toggleMeasurement = (measurementName: string): void => {
        this.selectedMeasurementNames[measurementName] = !this.selectedMeasurementNames[measurementName];
        this.updateSelectionState();
    };

    private toggleAllSelected = (e: Event): void => {
        const select = !this.allSelected && !this.anySelected;

        // Update all
        for (const measurement of this.spectra) {
            this.selectedMeasurementNames[measurement.options.id] = select;
        }
        this.updateSelectionState();
    };

    private updateSelectionState(): void {
        let allSelected = true;
        let anySelected = false;

        for (const group of this.spectra) {
            if (this.selectedMeasurementNames[group.options.id]) {
                anySelected = true;
                if (!allSelected) {
                    break;
                }
            } else {
                allSelected = false;
            }
        }

        this.anySelected = anySelected;
        this.allSelected = allSelected;

        if (!this.checkAllCheckbox) {
            return;
        }

        this.checkAllCheckbox.indeterminate = anySelected && !allSelected;
        this.checkAllCheckbox.checked = allSelected;
    }

    public exportCSV(): void {
        const exporter = new CSVDataExporter();
        this.selectedSpectra = this.spectra.filter((x) => this.selectedMeasurementNames[x.options.id] === true);
        let fileName: string;

        // Use Generic filename when more measurements are selected
        if (this.selectedSpectra.length > 1) {
            fileName = "Spectral_Analysis_Data_" + this.selectedWeighting + "-Weighting";
        } else {
            // Use the selected measurementname as filename
            fileName = this.selectedSpectra[0].options.name + "_" + this.selectedWeighting + "-Weighting";
        }

        const csv = exporter.exportSeries(
            this.selectedFrequencyAxisKind.unit,
            this.selectedSpectra.map((x) => {
                return <Series>{
                    data: x.data.map((p) => ({
                        x: p.x,
                        y: p.y,
                    })),
                    label: x.options.name,
                    yUnit: x.yUnit,
                };
            })
        );

        FileDownloader.downloadAsFile(csv, "text/plain", fileName + ".csv");

        this.selectedMeasurementNames = {};
        this.updateSelectionState();
    }

    private cancelButton(): void {
        this.modalInstance.cancel();
    }
}

// View-Model for modal needs to be first class in the file
export class CSVExportModalState {
    public spectra: PlotData[];
    public selectedFrequencyAxisKind: AxisKind;
    public selectedWeighting: string;
}

export class CSVExportModalModel {
    public state: CSVExportModalState;
    public moduleInstance: IWorkflowModuleInstance;
}
